import { historyDate } from './fiscal-history.view';

type Data = Record<string, unknown>;
const object = (value: unknown): Data | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Data : null;
const amount = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;
const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value : null;
const count = (value: unknown): number | null => Number.isSafeInteger(value) && (value as number) >= 0 ? value as number : null;

export interface ReportPayment {
  id: string;
  name: string | null;
  code: number | null;
  sales: number | null;
  returns: number | null;
}
export interface ReportTax {
  id: string;
  name: string | null;
  letter: string | null;
  code: number | null;
  sales: number | null;
  returns: number | null;
  saleTax: number | null;
  returnTax: number | null;
  saleExcise: number | null;
  returnExcise: number | null;
}

/** Report money is already in UAH, just like the receipt sums. Missing values are not zero. */
export function fiscalReportView(value: unknown) {
  const info = object(value);
  if (!info) return null;
  const receipt = object(info['receipt']) ?? {};
  const summary = object(info['summary']) ?? {};
  const pays = Array.isArray(info['pays']) ? info['pays'] : null;
  const payments: ReportPayment[] = (pays ?? []).map((raw, index) => {
    const row = object(raw) ?? {};
    return { id: `pay-${index}`, name: text(row['name']), code: count(row['type']), sales: amount(row['sum_p']), returns: amount(row['sum_m']) };
  });
  const total = (field: 'sales' | 'returns'): number | null => {
    if (pays === null || payments.some((row) => row[field] === null)) return null;
    const sum = payments.reduce((result, row) => result + row[field]!, 0);
    const rounded = Math.round(sum * 100) / 100;
    return Number.isFinite(rounded) ? rounded : null;
  };
  const taxes: ReportTax[] = (Array.isArray(info['taxes']) ? info['taxes'] : []).map((raw, index) => {
    const row = object(raw) ?? {};
    return {
      id: `tax-${index}`, name: text(row['tax_fname']) ?? text(row['tax_name']), letter: text(row['tax_lit']), code: count(row['gr_code']),
      sales: amount(row['base_sum_p']), returns: amount(row['base_sum_m']),
      saleTax: amount(row['tax_sum_p']), returnTax: amount(row['tax_sum_m']),
      saleExcise: amount(row['ex_sum_p']), returnExcise: amount(row['ex_sum_m']),
    };
  });
  return {
    generatedAt: historyDate(info['dt']), number: count(info['docno']),
    sales: total('sales'), returns: total('returns'), cashBalance: amount(info['safe']),
    saleCount: count(receipt['count_p']), returnCount: count(receipt['count_m']),
    saleDiscount: amount(summary['disc_p']), returnDiscount: amount(summary['disc_m']),
    payments, taxes,
  };
}
export type FiscalReportView = ReturnType<typeof fiscalReportView>;
